import React, { useEffect, useState } from "react";
import AdReportCharts from "./AdReportCharts";

// Seller-facing ad performance for one campaign / ad set / ad, from the API's insights endpoints.
// `load(params)` is one of getCampaignInsights/getAdSetInsights/getAdInsights bound to an id.

const RANGES = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
];

const isoDay = (date) => date.toISOString().slice(0, 10);

export function rangeParams(days) {
  const to = new Date();
  const from = new Date(to.getTime() - (days - 1) * 86400000);
  return { from: isoDay(from), to: isoDay(to) };
}

export function formatMoney(value, currency) {
  if (value === null || value === undefined) return "-";
  const amount = Number(value);
  // Ad costs per impression are fractions of a cent, so small amounts keep more decimals.
  const digits = amount !== 0 && Math.abs(amount) < 1 ? 4 : 2;
  try {
    if (currency) {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: digits,
      }).format(amount);
    }
  } catch {
    // Unknown currency code - fall through to the plain format.
  }
  return `$ ${amount.toFixed(digits)}`;
}

const formatCount = (value) => (value === null || value === undefined ? "-" : Number(value).toLocaleString());
const formatRatio = (value, suffix = "") => (value === null || value === undefined ? "-" : `${Number(value).toFixed(2)}${suffix}`);

function Tile({ label, value, hint }) {
  return (
    <div className="bg-white rounded-xl p-3" title={hint}>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-lg font-bold text-[#131b2e] break-words">{value}</p>
    </div>
  );
}

function Section({ title, bg, children }) {
  return (
    <div className={`rounded-2xl p-4 mb-4 ${bg}`}>
      <h3 className="font-bold text-gray-900 mb-3">{title}</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{children}</div>
    </div>
  );
}

export default function AdInsightsPanel({ load, showCharts = false }) {
  const [days, setDays] = useState(30);
  const [insights, setInsights] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setError(false);
    load({ ...rangeParams(days), breakdown: "day" })
      .then((result) => active && setInsights(result))
      .catch(() => active && setError(true));
    return () => {
      active = false;
    };
  }, [load, days]);

  const totals = insights?.totals;
  const currency = insights?.currency;
  const conversions = totals?.conversions || {};
  const otherCurrencies = Object.entries(totals?.other_currency_purchase_value || {});

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <h2 className="text-lg font-semibold">Performance</h2>
        <div className="flex gap-1.5 bg-[#ebf8ed] rounded-full p-1">
          {RANGES.map((range) => (
            <button
              key={range.days}
              type="button"
              onClick={() => setDays(range.days)}
              className={`px-3 py-1 rounded-full text-xs font-semibold ${
                days === range.days ? "bg-[#095228] text-white" : "text-[#095228]"
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="text-sm text-[#ba1a1a] mb-4">Couldn't load performance data.</p>}
      {!error && !totals && <p className="text-sm text-[#747474] mb-4">Loading performance...</p>}

      {totals && (
        <>
          <Section title="Delivery" bg="bg-[#EAF6EE]">
            <Tile label="Impressions" value={formatCount(totals.impressions)} hint="Ad slots shown (billed)" />
            <Tile label="Reach" value={formatCount(totals.reach)} hint="Distinct people who saw the ad" />
            <Tile label="Frequency" value={formatRatio(totals.frequency)} hint="Average impressions per person" />
            <Tile label="Clicks" value={formatCount(totals.clicks)} />
            <Tile label="CTR" value={formatRatio(totals.ctr, "%")} hint="Clicks / impressions" />
            <Tile
              label="Viewable impressions"
              value={formatCount(totals.viewable_impressions)}
              hint="At least half the ad on screen for one second"
            />
          </Section>

          <Section title="Cost" bg="bg-[#FBF6DD]">
            <Tile label="Amount spent" value={formatMoney(totals.spend, currency)} />
            <Tile label="CPM" value={formatMoney(totals.cpm, currency)} hint="Cost per 1,000 impressions" />
            <Tile label="CPC" value={formatMoney(totals.cpc, currency)} hint="Cost per click" />
            <Tile label="CPA" value={formatMoney(totals.cpa, currency)} hint="Cost per purchase" />
          </Section>

          <Section title="Conversions" bg="bg-[#FBEAEA]">
            <Tile label="Product views" value={formatCount(conversions.product_view)} />
            <Tile label="Adds to cart" value={formatCount(conversions.add_to_cart)} />
            <Tile label="Purchases" value={formatCount(conversions.purchase)} />
            <Tile label="Messages" value={formatCount(conversions.message)} hint="Conversations started with you" />
            <Tile label="Follows" value={formatCount(conversions.follow)} />
          </Section>

          <Section title="Return on ads" bg="bg-[#E8F1FB]">
            <Tile label="Purchase value" value={formatMoney(totals.purchase_value, currency)} />
            <Tile
              label="Return on ad spend"
              value={totals.roas === null ? "-" : `${Number(totals.roas).toFixed(2)}x`}
              hint="Purchase value / amount spent"
            />
          </Section>

          <p className="text-xs text-[#747474] mb-4">
            Conversions are credited to your ad when the buyer clicked it within{" "}
            {insights.attribution.click_window_days} days, or viewed it within {insights.attribution.view_window_days}{" "}
            day{insights.attribution.view_window_days === 1 ? "" : "s"}, before converting.
            {otherCurrencies.length > 0 &&
              ` Sales in other currencies (${otherCurrencies
                .map(([code, value]) => formatMoney(value, code))
                .join(", ")}) aren't included in return on ad spend.`}
          </p>

          {showCharts && <AdReportCharts rows={insights.rows} currency={currency} />}
        </>
      )}
    </div>
  );
}
